import { FC, useState } from "react";
import { useAtomValue } from "jotai";

import { JunkSizeEnum, TaskDriverStatusEnum } from "@/domain/models/common";
import { JunkLoadStatusEnum } from "@/domain/models/job";

import { OptionType } from "../blocks";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { UpdateServicePropsType } from "../domain/service";
import { JobServiceTaskBaseType } from "../domain/task";
import {
  isDriverReviewingJunkLoadAtom,
  isDriverReviewingTaskAtom,
} from "../stores/atoms";
import { DriverScreenWithOptions } from "./DriverScreenWithOptions";

const SIZE_OPTIONS: OptionType<JunkSizeEnum>[] = [
  { label: "1/3", value: JunkSizeEnum.ONE_THIRD },
  { label: "2/3", value: JunkSizeEnum.TWO_THIRDS },
  { label: "3/3", value: JunkSizeEnum.FULL },
];

const DriverLoadSizeStep: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const [selectedSize, setSelectedSize] = useState<JunkSizeEnum | null>(null);

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);
  const isDriverReviewingJunkLoad = useAtomValue(isDriverReviewingJunkLoadAtom);

  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const getNextDriverFlowStatus = () => {
    switch (true) {
      case isDriverReviewingTask:
        return TaskDriverStatusEnum.TASK_REVIEW;
      case isDriverReviewingJunkLoad:
        return TaskDriverStatusEnum.JUNK_LOAD_REVIEW;
      default:
        return TaskDriverStatusEnum.FILLING_LOCATION_ACCESS;
    }
  };

  const onNextClick = () => {
    if (selectedSize) {
      const driverFlowStatus = getNextDriverFlowStatus();

      const updateServiceParams: UpdateServicePropsType = {
        jobId,
        serviceId,
        driverFlowStatus,
      };

      // Check if there's a list of junk loads
      const junkLoadInProgressIndex =
        serviceData?.junkLoads?.findIndex(
          junkLoad => junkLoad.status === JunkLoadStatusEnum.IN_PROGRESS,
        ) ?? -1;

      // If there's one in progress, means driver is managing data for that specific load
      if (junkLoadInProgressIndex !== -1 && serviceData?.junkLoads) {
        const updatedJunkLoad = serviceData.junkLoads[junkLoadInProgressIndex];

        updatedJunkLoad.loadSize = selectedSize;

        // Change back to completed status if driver is reviewing the task
        // this one is when driver is reviewing the task and pressed to edit the value
        if (isDriverReviewingTask) {
          updatedJunkLoad.status = JunkLoadStatusEnum.COMPLETED;
        }

        serviceData.junkLoads.splice(
          junkLoadInProgressIndex,
          1,
          updatedJunkLoad,
        );

        updateServiceParams.junkLoads = [...serviceData.junkLoads];
      } else {
        updateServiceParams.loadSize = selectedSize;
      }

      updateService(updateServiceParams);

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus,
      });
    }
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  return (
    <DriverScreenWithOptions
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_load_size_screen.title"
      optionsSelectorProps={{
        onSelect: setSelectedSize,
        value: selectedSize,
        options: SIZE_OPTIONS,
      }}
      bottomButtonProps={{
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        isNextButtonLoading: isLoading,
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: !selectedSize,
      }}
    />
  );
};

export { DriverLoadSizeStep };
